import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { TableComponent } from '../../../core/table/table.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { BackButtonComponent } from '../../../core/buttons/back-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { BaseComponent } from '../../../base/base.component';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { PhysicianAdvisorDetailResponse } from '../../../../models/api/view/claim/details/case-management/physician-advisor-detail-response';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-case-peer-review',
  imports: [
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    BackButtonComponent,
    PageHeaderComponent,
    ActionRowComponent,
  ],
  template: `
    <div class="tw-pb-2">
      <app-action-row>
        <app-back-button left1 (click)="onBackClick()"
          >Back to Physician/Peer Review List</app-back-button
        >
      </app-action-row>
    </div>
    <app-page-header label="Physician/Peer Review Detail" />

    @if (loading) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-user-doctor"
        label="Loading Physician/Peer Review"
        message="Retrieving physician advisor details"
      >
        <div class="tw-py-4">
          <app-progressbar />
        </div>
      </app-empty-state>
    } @else if (error) {
      <app-empty-state
        icon="fa-light fa-triangle-exclamation"
        label="An error has occurred"
      />
    } @else if (!dataSource) {
      <app-empty-state
        icon="fa-light fa-user-doctor"
        label="No data available"
        message="Physician advisor details could not be loaded"
      />
    } @else {
      <div
        class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4 tw-pt-6"
      >
        <div class="tw-w-full lg:tw-basis-2/3">
          <app-card
            label="Physician Advisor"
            emptyIcon="fa-light fa-user-doctor"
            emptyLabel="No Physician Advisors"
            [showEmptyState]="isPhysicianAdvisorEmpty"
          >
            <app-detail-panel [dataSource]="dataSource">
              <app-detail-item
                label="PA Name"
                name="paName"
                labelCase="none"
              ></app-detail-item>
              <app-detail-item
                label="Other Vendor"
                name="vendor"
              ></app-detail-item>
              <app-detail-item
                label="Peer Reviewer"
                name="peerReviewer"
              ></app-detail-item>
              <app-detail-item
                label="Fee"
                name="fee"
                format="c"
              ></app-detail-item>
              <app-detail-item
                label="Created By"
                name="createdBy"
              ></app-detail-item>
            </app-detail-panel>
          </app-card>
        </div>
        <div class="tw-w-full lg:tw-basis-1/3">
          <app-card
            label="Referral Reasons"
            emptyIcon="fa-light fa-clipboard-list"
            emptyLabel="No Referral Reasons"
            [showEmptyState]="isReferralReasonEmpty"
          >
            <app-detail-panel [dataSource]="dataSource">
              <app-detail-item label="Referral Reason" name="referralReason" />
              <app-detail-item label="Date Sent" name="dateSent" format="dt" />
              <app-detail-item label="Due Date" name="dueDate" format="d" />
              <app-detail-item
                label="Received Date"
                name="receivedDate"
                format="dt"
              />
            </app-detail-panel>
          </app-card>
        </div>
      </div>
      <div class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-8 tw-pb-4">
        <div class="tw-w-full lg:tw-basis-2/3">
          <app-card label="Documents">
            <app-table
              [dataSource]="dataSource?.images"
              [pageSize]="5"
              [scrollable]="false"
              recordLabel="Document"
            >
              <app-column label="Record Date" name="recordedDate" format="d" />
              <app-column label="DCN" name="dcn" />
              <app-column label="Description" name="description" />
            </app-table>
          </app-card>
        </div>
        <div class="tw-w-full lg:tw-basis-1/3">
          <app-card label="Outcomes">
            <app-table
              [dataSource]="dataSource?.outcomes"
              [pageSize]="5"
              [scrollable]="false"
              recordLabel="Outcome"
            >
              <app-column label="Outcome" name="description" />
            </app-table>
          </app-card>
        </div>
      </div>
    }
  `,
  styles: [],
})
export class CasePeerReviewComponent
  extends BaseComponent
  implements OnChanges
{
  @Input() physicianAdvisorId: number | null = null;
  @Output() backToList = new EventEmitter<void>();

  dataSource: any = null;
  loading = false;
  error: any = null;
  isPhysicianAdvisorEmpty = true;
  isReferralReasonEmpty = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['physicianAdvisorId'] && this.physicianAdvisorId) {
      this.loadPhysicianAdvisorDetail(this.physicianAdvisorId);
    }
  }

  @HandleLoadingState()
  private async loadPhysicianAdvisorDetail(
    physicianAdvisorId: number,
  ): Promise<void> {
    const claimKey = getClaimKey();
    const response = await this.api.caseManagement.getPhysicianAdvisorDetail(
      claimKey,
      physicianAdvisorId,
    );

    this.dataSource = this.mapResponseToDataSource(response);
    this.setEmptyStates();
  }

  private mapResponseToDataSource(
    response: PhysicianAdvisorDetailResponse,
  ): any {
    return {
      ...response,
      referralReason: response.referralReasons?.join(', '),
    };
  }

  onBackClick(): void {
    this.backToList.emit();
  }

  private setEmptyStates(): void {
    const ds = this.dataSource;
    this.isPhysicianAdvisorEmpty =
      !ds ||
      (!ds.paName &&
        !ds.vendor &&
        !ds.peerReviewer &&
        !ds.fee &&
        !ds.createdBy);
    this.isReferralReasonEmpty =
      !ds ||
      (!ds.referralReason && !ds.dateSent && !ds.dueDate && !ds.receivedDate);
  }
}
